import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import ActivityFilterSidebar from "./ActivityFilterSidebar";
import ActivityTable from "./ActivityTable";
import ActivityToolbar from "./ActivityToolbar";
import CandidatesPagination from "../CandidateWise/CandidatesPagination";
import useStageRows from "./useStageRows";
import useActivityFilterOptions from "./useActivityFilterOptions";
import {
  ACTIVITY_PAGE_SIZE, DRILL_PARAMS, EMPTY_ACTIVITY_FILTERS, SEARCH_DEBOUNCE_MS,
  buildStageRowsPayload, countActiveActivityFilters, drillOptionKeys, hiringStatusColor,
  parseDrillParams, resolveDrillFilters,
} from "./activityUtils";

const HiringStatusBadge = ({ name, color }) => {
  const c = hiringStatusColor(name, color);
  return <span className="activity-badge" style={{ color: c, background: `${c}18` }}>{name}</span>;
};

// One row per candidate stage, with filters on the left. Period + refresh are portalled into
// `toolbarSlot` (the right side of the Candidate-wise / Activity-wise tabs bar). Filtering, sorting and paging
// are done by stage-rows/search.
const ActivityWiseView = ({ jobId, access, toolbarSlot }) => {
  const options = useActivityFilterOptions(jobId);
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState(EMPTY_ACTIVITY_FILTERS);
  const [search, setSearch] = useState(""); // debounced copy of filters.search
  const [sort, setSort] = useState({ field: "assigned", dir: "desc" });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(ACTIVITY_PAGE_SIZE);

  // Deep link from Job Insights: read once, turn into filters as soon as the option lists it
  // needs have loaded, then drop the params from the URL so later filter changes aren't
  // overridden on refresh. Rows aren't fetched until this is done.
  const [drill] = useState(() => parseDrillParams(searchParams));
  const [drillApplied, setDrillApplied] = useState(!drill);
  const drillOptionsReady = !drill || drillOptionKeys(drill).every((k) => options[k] !== null);
  useEffect(() => {
    if (drillApplied || !drillOptionsReady) return;
    setFilters(resolveDrillFilters(drill, options));
    setDrillApplied(true);
    const next = new URLSearchParams(searchParams);
    DRILL_PARAMS.forEach((k) => next.delete(k));
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drillApplied, drillOptionsReady]);

  useEffect(() => {
    const t = setTimeout(() => setSearch(filters.search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [filters.search]);

  // Any filter/sort change goes back to page 1. Search is keyed on the debounced value
  // so typing doesn't jump pages before the request goes out.
  const filtersKey = JSON.stringify({ ...filters, search: undefined }); // undefined keys are dropped
  const resetKey = JSON.stringify([filtersKey, search, sort, pageSize]);
  useEffect(() => { setPage(1); }, [resetKey]);

  const payload = useMemo(
    () => buildStageRowsPayload(filters, { search, sort, page, pageSize }),
    // filters.search is deliberately excluded; `search` is its debounced copy.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filtersKey, search, sort, page, pageSize]
  );
  const { rows, total, loading, refreshing, refresh } = useStageRows(jobId, payload, drillApplied);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // A refresh can shrink the list; keep the page in range so ‹ / › step correctly.
  useEffect(() => { if (!loading && page > totalPages) setPage(totalPages); }, [loading, page, totalPages]);

  const setFilter = (key, value) => setFilters((f) => ({ ...f, [key]: value }));
  const toggleFilterValue = (key, value) => setFilters((f) => ({
    ...f,
    [key]: f[key].includes(value) ? f[key].filter((v) => v !== value) : [...f[key], value],
  }));
  const handleSort = (field) => setSort((s) => (s.field === field ? { field, dir: s.dir === "desc" ? "asc" : "desc" } : { field, dir: "desc" }));

  return (
    <div className="candidates-v2-page activity-wise-page">
      <ActivityFilterSidebar
        filters={filters}
        onChange={setFilter}
        onToggle={toggleFilterValue}
        // Period lives in the toolbar, so the sidebar's Reset leaves it alone.
        onReset={() => setFilters((f) => ({ ...EMPTY_ACTIVITY_FILTERS, period: f.period, dateRange: f.dateRange }))}
        options={options}
        activeCount={countActiveActivityFilters(filters)}
      />

      <main className="candidates-v2-main">
        {toolbarSlot && createPortal(
          <ActivityToolbar
            period={filters.period}
            dateRange={filters.dateRange}
            onPeriodChange={(v) => setFilter("period", v)}
            onDateRangeChange={(v) => setFilter("dateRange", v)}
            onRefresh={refresh}
            refreshing={refreshing}
            disabled={loading || !drillApplied}
          />,
          toolbarSlot
        )}
        <div className="candidates-table-outer">
          <ActivityTable
            jobId={jobId}
            rows={rows}
            loading={loading}
            sort={sort}
            onSort={handleSort}
            canAccessCandidate={access.canAccessCandidate}
            hiringStatusBadge={(row) => <HiringStatusBadge name={row.hiringStatus} color={row.hiringStatusColor} />}
          />
          {!loading && (
            <CandidatesPagination
              pagination={{ total, page, page_size: pageSize }}
              totalPages={totalPages}
              currentPage={page}
              setCurrentPage={setPage}
              pageSize={pageSize}
              setPageSize={setPageSize}
              itemLabel="stages"
            />
          )}
        </div>
      </main>
    </div>
  );
};

export default ActivityWiseView;
